import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-black/10">
          <SheetTitle className="font-display text-2xl tracking-tight text-left flex items-center gap-2">
            <ShoppingBag size={20} /> Your Bag ({items.length})
          </SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping bag</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 && (
            <p className="text-muted-foreground text-sm" data-testid="cart-empty">
              Your bag is empty. Discover the collection.
            </p>
          )}
          {items.map((it) => (
            <div key={it.slug} className="flex gap-4" data-testid={`cart-item-${it.slug}`}>
              <div className="w-24 h-28 bg-[#F5F5F5] rounded-xl overflow-hidden shrink-0">
                <img src={`${it.image}?w=300&q=80`} alt={it.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-display text-lg leading-tight">{it.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatINR(it.price)}</p>
                  </div>
                  <button onClick={() => removeItem(it.slug)} data-testid={`cart-remove-${it.slug}`} className="text-muted-foreground hover:text-black transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateQty(it.slug, it.quantity - 1)} data-testid={`cart-dec-${it.slug}`}
                    className="w-8 h-8 border border-black/15 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                    <Minus size={12} />
                  </button>
                  <span className="text-sm w-6 text-center">{it.quantity}</span>
                  <button onClick={() => updateQty(it.slug, it.quantity + 1)} data-testid={`cart-inc-${it.slug}`}
                    className="w-8 h-8 border border-black/15 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-black/10 p-6 space-y-4">
            <div className="flex justify-between text-sm uppercase tracking-widest">
              <span className="text-muted-foreground">Subtotal</span>
              <span data-testid="cart-total">{formatINR(total)}</span>
            </div>
            <button onClick={goCheckout} data-testid="cart-checkout-btn"
              className="w-full bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
              Secure Checkout
            </button>
            <button onClick={() => { setOpen(false); navigate("/cart"); }} data-testid="cart-view-bag"
              className="w-full text-xs uppercase tracking-[0.2em] link-underline mx-auto block text-center pt-1">
              View full bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
